import { Fragment, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  IDatahubCase,
  IDatahubTask,
  IGetTaskListParams,
  ISubmitNoteOptions,
  ISimplifiedOrder,
  TaskCategory,
  TasksApi,
  TaskStatus,
} from 'api';
import { useLocalization } from 'localization';
import { useShowModal } from 'modules/common/GlobalModals';
import QUERIES from 'modules/common/queries';
import { useParsedFilters } from 'modules/common/hooks';
import { TaskModalCommonProps } from './types';
import { useScreenMatch } from 'theme';
import { useAuth } from 'modules/auth/hooks';

export function useTaskFilterOptions() {
  const { t } = useLocalization();
  const parsedQueryString = useParsedFilters<{
    branchIds: string[];
    category?: string[];
    status?: string[];
    query?: string;
  }>('tasks');
  const { data } = useQuery({ queryKey: [QUERIES.taskFilters], queryFn: TasksApi.getFilters });

  // empty qs should be reinitialized with ALL filters selected
  // query is ignored in order to be able to search if this is the only parameter
  const isQSEmpty = useMemo(
    () => !parsedQueryString.category && !parsedQueryString.branchIds && !parsedQueryString.status,
    [parsedQueryString]
  );

  const categoryOptions = useMemo(
    () =>
      data?.categories.map(item => ({
        value: item,
        label: t(`tasks.categories.${item}`),
      })) ?? [],
    [data, t]
  );
  const categoryValues = useMemo(() => {
    const qsArray = parsedQueryString.category as TaskCategory[];

    if (qsArray && categoryOptions.length > 0) {
      return categoryOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value);
    } else if (!qsArray) {
      return isQSEmpty ? categoryOptions.map(option => option.value) : [];
    }
    return qsArray;
  }, [categoryOptions, parsedQueryString, isQSEmpty]);

  const statusOptions = useMemo(
    () =>
      data?.statuses.map(item => ({
        value: item,
        label: t(`tasks.statuses.${item}`),
      })) ?? [],
    [data, t]
  );
  const statusValues = useMemo(() => {
    const qsArray = parsedQueryString.status as TaskStatus[];

    if (qsArray && statusOptions.length > 0) {
      return statusOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value);
    } else if (!qsArray) {
      return isQSEmpty ? statusOptions.map(option => option.value) : [];
    }
    return qsArray;
  }, [statusOptions, parsedQueryString, isQSEmpty]);

  const departmentOptions = useMemo(
    () =>
      data?.branches.map(item => ({
        value: item.id,
        label: item.name,
      })) ?? [],
    [data]
  );
  const departmentValues = useMemo(() => {
    const qsArray = parsedQueryString.branchIds;

    if (qsArray && departmentOptions.length > 0) {
      return departmentOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value);
    } else if (!qsArray) {
      return isQSEmpty ? departmentOptions.map(option => option.value) : [];
    }
    return qsArray;
  }, [departmentOptions, parsedQueryString, isQSEmpty]);

  return {
    categoryOptions,
    statusOptions,
    departmentOptions,
    categoryValues,
    statusValues,
    departmentValues,
    parsedQueryString,
  };
}

export function useTasksQueriesInvalidation() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.taskFilters] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.totalTasksByReference] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.totalTasksForUser] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.orderTasks] }); // tasks source for reciept page
    queryClient.invalidateQueries({ queryKey: [QUERIES.getOrder] }); // task list presented in order details also, i.e. receipt page
    queryClient.invalidateQueries({ queryKey: [QUERIES.orderLines] }); // task list presented in order details also, i.e. receipt page
  }, [queryClient]);
}

export const useInputForSpeak = (caseId: IDatahubCase['id'], enabled = true) =>
  useQuery({
    queryKey: [QUERIES.inputForSpeakSlideShowText, caseId],
    queryFn: async () => {
      const groupedTexts = await TasksApi.getInputForSpeakSlideShowText(caseId);

      return groupedTexts
        .reduce(
          (memo, next) =>
            memo +
            ' ' +
            next.texts
              .reduce((textMemo, textNext) => (textNext.content ? `${textMemo} ${textNext.content}` : textMemo), '')
              .trim(),
          ''
        )
        .trim();
    },
    enabled,
  });

export function useTasks(params: IGetTaskListParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: [QUERIES.tasks, params],
    queryFn: ({ pageParam, signal }) =>
      TasksApi.getList({
        ...params,
        page: pageParam,
        signal,
      }),
    initialPageParam: 0,
    enabled,
    getNextPageParam: ({ page, pageSize, totalItems }) => {
      return (page + 1) * pageSize < totalItems ? page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
  });
}

export function useOrderTasks(orderId?: ISimplifiedOrder['id']) {
  return useQuery({
    queryKey: [QUERIES.orderTasks, orderId],
    queryFn: async () => (orderId ? await TasksApi.getOrderTasks(orderId) : undefined),
    enabled: !!orderId,
  });
}

export function useUploadsByTask(taskId: IDatahubTask['id']) {
  return useQuery({
    queryKey: [QUERIES.uploadsByTaskId, taskId],
    queryFn: async () => (taskId ? await TasksApi.getUploads(taskId) : undefined),
    enabled: !!taskId,
  });
}
